// Parsing and normalization for pasted workflow graph JSON.
//
// The canonical shape is whatever the editor's "View JSON" button emits:
//
//   {
//     "nodes": [
//       { "id": "node-<uuid>", "type": "llmNode", "position": { "x": 250, "y": 100 },
//         "data": { "label": "LLM", "prompt": "...", ... }, "width": 500, "height": "auto" }
//     ],
//     "edges": [
//       { "id": "edge-<src>-<srcHandle>-to-<tgt>-<tgtHandle>", "source": "...", "sourceHandle": "...",
//         "target": "...", "targetHandle": "...", "label": "..." }
//     ]
//   }
//
// Import is deliberately tolerant of the cosmetic variations that are easy to produce when
// generating JSON elsewhere (missing ids, top-level x/y, bare arrays, a stringified graph,
// nodes referenced by name), but it rejects anything structurally ambiguous rather than guessing.

const MAX_WRAPPER_DEPTH = 5;

const NODE_TYPE_LABELS = {
  inputNode: "Input",
  inputDocumentNode: "Input Document",
  textOutputNode: "Text Output",
  llmNode: "LLM",
  pythonNode: "Python",
  conditionalNode: "Conditional",
  routerNode: "Router",
  dataSetNode: "Data Set",
  databaseNode: "Database",
  chromaInputNode: "Chroma Input",
  chromaDatabaseNode: "Chroma Database",
  textSplittingNode: "Text Splitting",
  generateQuestionsNode: "Generate Questions",
  workflowNode: "Workflow",
};

const isPlainObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const toFiniteNumber = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
};

const isEmptyId = (value) =>
  value === undefined || value === null || String(value).trim() === "";

// Nodes may arrive as an array, or as an object keyed by id (the older Konva editor and
// several server payloads use that shape). Normalize both to an array.
const nodesToArray = (rawNodes) => {
  if (Array.isArray(rawNodes)) return rawNodes;
  if (isPlainObject(rawNodes)) {
    return Object.entries(rawNodes).map(([key, node]) =>
      isPlainObject(node) ? { ...node, id: node.id ?? key } : node
    );
  }
  return null;
};

const edgesToArray = (rawEdges) => {
  if (rawEdges === undefined || rawEdges === null) return [];
  if (Array.isArray(rawEdges)) return rawEdges;
  // Edges are occasionally serialized as an id-keyed map too.
  if (isPlainObject(rawEdges)) return Object.values(rawEdges);
  return null;
};

const describeKeys = (value) => {
  const keys = isPlainObject(value) ? Object.keys(value) : [];
  return keys.length > 0 ? ` Found top-level keys: ${keys.join(", ")}.` : "";
};

// Accepts `{nodes, edges}`, `{graph: {nodes, edges}}`, `{graph: "<json>"}`, or a bare node array.
const extractGraph = (parsed, depth = 0) => {
  if (depth > MAX_WRAPPER_DEPTH) {
    throw new Error("The JSON is nested too deeply to be a workflow graph.");
  }

  if (Array.isArray(parsed)) return { nodes: parsed, edges: [] };

  if (!isPlainObject(parsed)) {
    throw new Error(
      `Expected a JSON object with "nodes" and "edges", but found ${Array.isArray(parsed) ? "an array" : typeof parsed}.`
    );
  }

  if (parsed.nodes !== undefined && parsed.nodes !== null) {
    return { nodes: parsed.nodes, edges: parsed.edges ?? [] };
  }

  if (parsed.graph !== undefined && parsed.graph !== null) {
    if (typeof parsed.graph === "string") {
      let inner;
      try {
        inner = JSON.parse(parsed.graph);
      } catch (error) {
        throw new Error(
          `The "graph" property is a string but not valid JSON: ${error.message}`
        );
      }
      return extractGraph(inner, depth + 1);
    }
    return extractGraph(parsed.graph, depth + 1);
  }

  throw new Error(
    `Could not find a graph in this JSON.${describeKeys(parsed)}` +
      ' Expected an object with a "nodes" array (and optionally "edges").'
  );
};

const normalizeNodes = (rawNodes, { validNodeTypes = [], callbacks = {} }) => {
  const warnings = [];
  const nodes = nodesToArray(rawNodes);

  if (nodes === null) {
    throw new Error('"nodes" must be an array, or an object keyed by node id.');
  }

  const usedIds = new Set();
  const unknownTypes = new Set();
  const normalized = [];
  let missingPositionCount = 0;
  let missingTypeCount = 0;

  nodes.forEach((rawNode, index) => {
    if (!isPlainObject(rawNode)) {
      warnings.push(`Node at index ${index} is not an object and was skipped.`);
      return;
    }

    // ---- id (React Flow requires a stable, unique id to address a node) ----------------
    // When generating JSON by hand it is easy to omit ids and wire edges up by `name`
    // instead, so a name is accepted verbatim as the id (React Flow ids are free-form
    // strings, spaces included) and any edge pointing at that name still resolves.
    let id =
      rawNode.id ??
      rawNode.graph_id ??
      rawNode.public_id ??
      rawNode.name ??
      (isPlainObject(rawNode.data) ? rawNode.data.label : undefined);
    if (isEmptyId(id)) {
      id = `node-imported-${index}`;
      warnings.push(`Node at index ${index} had no id; assigned "${id}".`);
    }
    id = String(id);

    if (usedIds.has(id)) {
      // Keep the first occurrence so existing edges still resolve, rename the rest.
      let candidate = `${id}-${index}`;
      while (usedIds.has(candidate)) candidate = `${candidate}-${index}`;
      warnings.push(
        `Duplicate node id "${id}" was imported as "${candidate}"; edges still point at the first one.`
      );
      id = candidate;
    }
    usedIds.add(id);

    // ---- position (fall back to a tidy grid so nothing lands at the origin) ------------
    const positionSource = isPlainObject(rawNode.position)
      ? rawNode.position
      : rawNode;
    let x = toFiniteNumber(positionSource.x);
    let y = toFiniteNumber(positionSource.y);

    if (x === null || y === null) {
      missingPositionCount += 1;
      x = 100 + (index % 4) * 280;
      y = 100 + Math.floor(index / 4) * 200;
    }

    // ---- type (unknown types render as generic nodes instead of crashing) --------------
    let type = rawNode.type;
    if (isEmptyId(type)) {
      missingTypeCount += 1;
      type = undefined;
    } else {
      type = String(type);
      if (validNodeTypes.length > 0 && !validNodeTypes.includes(type)) {
        unknownTypes.add(type);
        type = undefined;
      }
    }

    // ---- data (carry every field through; the node components read from it) -----------
    let data = {};
    if (isPlainObject(rawNode.data)) {
      data = { ...rawNode.data };
    } else if (rawNode.data !== undefined && rawNode.data !== null) {
      warnings.push(
        `Node "${id}" had a non-object "data" value; replaced with an empty object.`
      );
    }

    if (isEmptyId(data.label)) {
      data.label = (type && NODE_TYPE_LABELS[type]) || type || "Node";
    }

    // Runtime-only handlers. The editor wires these onto every node it loads or creates;
    // imported nodes need them too or their close/update controls silently do nothing.
    if (callbacks.onDelete) data.onDelete = callbacks.onDelete;
    if (callbacks.onUpdateData) data.onUpdateData = callbacks.onUpdateData;
    if (callbacks.onRenameInput) data.onRenameInput = callbacks.onRenameInput;

    const node = {
      ...rawNode,
      id,
      position: { x, y },
      data,
      deletable: rawNode.deletable !== false,
      dragHandle: rawNode.dragHandle ?? ".node-drag-handle",
      resizable: rawNode.resizable !== false,
      resizeHandles:
        Array.isArray(rawNode.resizeHandles) && rawNode.resizeHandles.length > 0
          ? rawNode.resizeHandles
          : ["se"],
    };

    // x/y at the top level is a legacy artifact we just lifted into `position`.
    delete node.x;
    delete node.y;

    if (type === undefined) {
      delete node.type;
    } else {
      node.type = type;
    }

    normalized.push(node);
  });

  if (missingPositionCount > 0) {
    warnings.push(
      `${missingPositionCount} node(s) had no usable position; laid out in a grid.`
    );
  }
  if (missingTypeCount > 0) {
    warnings.push(
      `${missingTypeCount} node(s) had no type; they will render as generic nodes.`
    );
  }
  if (unknownTypes.size > 0) {
    warnings.push(
      `Unknown node type(s) ${[...unknownTypes].map((t) => `"${t}"`).join(", ")} will render as generic nodes.`
    );
  }

  return { nodes: normalized, warnings };
};

const normalizeEdges = (rawEdges, nodeIds) => {
  const warnings = [];
  const edges = edgesToArray(rawEdges);

  if (edges === null) {
    throw new Error('"edges" must be an array (or omitted).');
  }

  const usedIds = new Set();
  const normalized = [];
  let missingEndpointCount = 0;

  edges.forEach((rawEdge, index) => {
    if (!isPlainObject(rawEdge)) {
      warnings.push(`Edge at index ${index} is not an object and was skipped.`);
      return;
    }

    const source = rawEdge.source ?? rawEdge.sourceId;
    const target = rawEdge.target ?? rawEdge.targetId;

    if (isEmptyId(source) || isEmptyId(target)) {
      if (isPlainObject(rawEdge.output) || isPlainObject(rawEdge.input)) {
        throw new Error(
          `Edge at index ${index} uses the legacy "output"/"input" shape from the old Konva editor. ` +
            'Re-export it with "source" and "target" fields (open "View JSON" on any graph for an example).'
        );
      }
      throw new Error(
        `Edge at index ${index} is missing "source" and/or "target". Every edge needs both.`
      );
    }

    const sourceId = String(source);
    const targetId = String(target);

    if (!nodeIds.has(sourceId) || !nodeIds.has(targetId)) {
      missingEndpointCount += 1;
      return;
    }

    const sourceHandle = rawEdge.sourceHandle;
    const targetHandle = rawEdge.targetHandle;

    let id = rawEdge.id;
    if (isEmptyId(id)) {
      // Mirrors the id format used by onConnect.
      id = `edge-${sourceId}-${sourceHandle ?? "default"}-to-${targetId}-${targetHandle ?? "default"}`;
    }
    id = String(id);

    if (usedIds.has(id)) {
      let candidate = `${id}-${index}`;
      while (usedIds.has(candidate)) candidate = `${candidate}-${index}`;
      warnings.push(`Duplicate edge id "${id}" was imported as "${candidate}".`);
      id = candidate;
    }
    usedIds.add(id);

    const edge = { ...rawEdge, id, source: sourceId, target: targetId };

    if (isEmptyId(edge.label) && !isEmptyId(sourceHandle)) {
      edge.label = sourceHandle;
    }

    normalized.push(edge);
  });

  if (missingEndpointCount > 0) {
    warnings.push(
      `${missingEndpointCount} edge(s) referenced nodes that are not in this graph and were dropped.`
    );
  }

  return { edges: normalized, warnings };
};

/**
 * Parse and normalize a workflow graph pasted by the user.
 *
 * @param {string} text Raw JSON text.
 * @param {object} [options]
 * @param {string[]} [options.validNodeTypes] Registered node types; anything else is
 *   downgraded to a generic node rather than throwing at render time.
 * @param {object} [options.callbacks] Runtime handlers to graft onto each node's data:
 *   `{ onDelete, onUpdateData, onRenameInput }`.
 * @returns {{nodes: object[], edges: object[], warnings: string[]}}
 * @throws {Error} with a user-facing message when the JSON cannot be interpreted.
 */
export const parseGraphJSON = (text, options = {}) => {
  if (typeof text !== "string" || text.trim() === "") {
    throw new Error("Paste the graph JSON first.");
  }

  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch (error) {
    throw new Error(`That is not valid JSON: ${error.message}`);
  }

  const { nodes: rawNodes, edges: rawEdges } = extractGraph(parsed);

  const nodeResult = normalizeNodes(rawNodes, options);
  const nodeIds = new Set(nodeResult.nodes.map((node) => node.id));
  const edgeResult = normalizeEdges(rawEdges, nodeIds);

  return {
    nodes: nodeResult.nodes,
    edges: edgeResult.edges,
    warnings: [...nodeResult.warnings, ...edgeResult.warnings],
  };
};

export default parseGraphJSON;
